// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import loadAddressFields from "@insite/client-framework/Store/Data/AddressFields/Handlers/LoadAddressFields";
import loadCurrentCountries from "@insite/client-framework/Store/Data/Countries/Handlers/LoadCurrentCountries";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { ExcludeFromNavigation } from "@insite/client-framework/Types/FieldDefinition";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import Page from "@insite/mobius/Page";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import { xIsAuthenticated } from "@rpsc/App/Store/Selectors/GeneralSelectors";
import { Step } from "@rpsc/App/Widgets/Stepper/helpers";
import StepperContextProvider from "@rpsc/App/Widgets/Stepper/StepperContext";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";

export const guestAccountSteps: Step[] = [
    {
        index: 1,
        name: `${translate("Basic Information")}`,
        prev: 0,
        displayName: `${translate("Basic Information")}`,
        next: 2,
    },
    {
        index: 2,
        name: `${translate("Billing & Shipping")}`,
        prev: 1,
        displayName: `${translate("Billing & Shipping")}`,
    },
];

const mapStateToProps = (state: ApplicationState) => ({
    isAuthenticated: xIsAuthenticated(state),
    myAccountPageUrl: getPageLinkByPageType(state, "MyAccountPage")?.url,
});

const mapDispatchToProps = {
    loadCurrentCountries,
    loadAddressFields,
};

type GuestAccountRegistrationPageProps = PageProps &
    ResolveThunks<typeof mapDispatchToProps> &
    ReturnType<typeof mapStateToProps> &
    HasHistory;

const GuestAccountRegistrationPage: React.FC<GuestAccountRegistrationPageProps> = ({
    id,
    isAuthenticated,
    myAccountPageUrl,
    history,
    loadCurrentCountries,
    loadAddressFields,
}) => {
    useEffect(() => {
        loadCurrentCountries();
        loadAddressFields();

        if (isAuthenticated) {
            myAccountPageUrl && history.push(myAccountPageUrl);
        }
    }, []);

    return (
        <Page>
            <StepperContextProvider steps={guestAccountSteps} allowStepClick={true}>
                <Zone contentId={id} zoneName="Content"></Zone>
            </StepperContextProvider>
        </Page>
    );
};

const GuestAccountRegistrationPageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(GuestAccountRegistrationPage)),
    definition: {
        hasEditableUrlSegment: true,
        hasEditableTitle: true,
        pageType: "Content",
        fieldDefinitions: [ExcludeFromNavigation],
    },
};

export default GuestAccountRegistrationPageModule;

export const GuestAccountRegistrationPageContext = "GuestAccountRegistrationPage";
